/**
 * PSP deep links: bank rows for Amazon/PayPal only name the PSP — the actual
 * purchase lives in the PSP's own account history. Detect the PSP from
 * counterparty/purpose and link to that record: Amazon order details via the
 * `xxx-xxxxxxx-xxxxxxx` order number in the purpose; PayPal's activity search
 * prefilled with the merchant from "… Ihr Einkauf bei <Händler>" (there is no
 * public per-transaction URL — the 13-digit reference in the SEPA purpose is
 * not PayPal's transaction id; worst case the search param is ignored and the
 * user lands on the plain activity list).
 */

import type { IsoDate } from './types.ts';

export interface OrderLink {
  href: string;
  /** Chip label, e.g. "Amazon-Bestellung". */
  label: string;
}

const AMAZON_ORDER_ID = /\b(\d{3}-\d{7}-\d{7})\b/;
const PAYPAL_MERCHANT = /Ihr Einkauf bei ([^,]+)/i;

const PAYPAL_ACTIVITIES = 'https://www.paypal.com/myaccount/activities';
// PayPal's free-text search only covers the displayed date range (default: last
// 90 days), so old transactions need an explicit window. The bank booking lags
// the PayPal activity by a few days — open wider before than after.
const PAYPAL_DAYS_BEFORE = 30;
const PAYPAL_DAYS_AFTER = 7;

function shiftIsoDate(iso: IsoDate, days: number): IsoDate {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function orderLink(txn: {
  counterparty_name: string | null;
  purpose: string | null;
  booking_date: IsoDate;
}): OrderLink | null {
  const purpose = txn.purpose ?? '';
  const counterparty = txn.counterparty_name ?? '';

  if (/amazon|amzn/i.test(`${counterparty} ${purpose}`)) {
    const order = purpose.match(AMAZON_ORDER_ID);
    if (order) {
      return {
        href: `https://www.amazon.de/gp/your-account/order-details?orderID=${order[1]}`,
        label: 'Amazon-Bestellung',
      };
    }
  }

  if (/paypal/i.test(counterparty)) {
    const merchant = purpose.match(PAYPAL_MERCHANT)?.[1].trim().replace(/[.,]+$/, '');
    const dates = `start_date=${shiftIsoDate(txn.booking_date, -PAYPAL_DAYS_BEFORE)}&end_date=${shiftIsoDate(txn.booking_date, PAYPAL_DAYS_AFTER)}`;
    return {
      href: merchant
        ? `${PAYPAL_ACTIVITIES}?free_text_search=${encodeURIComponent(merchant)}&${dates}`
        : `${PAYPAL_ACTIVITIES}?${dates}`,
      label: 'PayPal-Aktivität',
    };
  }

  return null;
}
